import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Notification from 'components/notification/Notification';
import dayjs from 'dayjs';
import { isIterableArray } from 'helpers/utils';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Card, Dropdown, ListGroup } from 'react-bootstrap';
import { ImHeartBroken } from 'react-icons/im';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import {
  getNotifications,
  markAllAsRead,
  markAllAsSeen,
  markAsRead
} from 'redux/notificationsSlice';
import messageHandler from 'utils/messageHandler';

export const getOutLink = info => {
  switch (info.type) {
    case 'search':
      return process.env.REACT_APP_CLIENT + '/propiedades/  ' + info.path;
  }
};

export const getInLink = info => {
  switch (info.type) {
    default:
      return '';
  }
};

export const getEmoji = type => {
  switch (type) {
    case 'search':
      return '🔍';

    case 'message':
      return '💬';

    default:
      return '🔔';
  }
};

export const getTime = time => {
  const now = dayjs();
  const date = dayjs(time).subtract(3, 'hour');

  if (now.diff(date, 'seconds') < 60) {
    return 'Justo ahora';
  } else if (now.diff(date, 'minutes') < 60) {
    return (
      `Hace ${now.diff(date, 'minutes')}` +
      (now.diff(date, 'minutes') === 1 ? ' minuto' : ' minutos')
    );
  } else if (now.diff(date, 'hours') < 24) {
    return (
      `Hace ${now.diff(date, 'hours')}` +
      (now.diff(date, 'hours') === 1 ? ' hora' : ' horas')
    );
  } else {
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  }
};

const NotificationDropdown = () => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isAllSeen, setIsAllSeen] = useState(true);
  const [newNotifications, setNewNotifications] = useState([]);
  const [earlierNotifications, setEarlierNotifications] = useState([]);

  const userLogged = useSelector(state => state.login.currentUser);
  const { socket } = useSelector(state => state.login);
  const { notifications } = useSelector(state => state.notifications);

  const dispatch = useDispatch();

  // Handler
  const handleToggle = () => {
    !isOpen && dispatch(markAllAsSeen(userLogged.id));
    setIsOpen(!isOpen);
  };

  const handleClick = notification => {
    setNewNotifications(
      newNotifications.map(n =>
        n.id === notification.id ? { ...n, unread: false } : n
      )
    );
    setEarlierNotifications(
      earlierNotifications.map(n =>
        n.id === notification.id ? { ...n, unread: false } : n
      )
    );

    handleToggle();
    dispatch(markAsRead(notification.id));
  };

  useEffect(() => {
    window.addEventListener('scroll', () => {
      window.innerWidth < 1200 && setIsOpen(false);
    });
  }, []);

  useEffect(() => {
    const unseenNotifications = notifications.filter(n => !n.seen);
    const seenNotifications = notifications.filter(n => n.seen);

    setNewNotifications(
      unseenNotifications.map(n => ({
        id: n.id,
        children: `<strong>${n.title}</strong>: ${n.body}`,
        time: getTime(n.created_at),
        emoji: getEmoji(n.info.type), // n.info.type
        className: 'rounded-0 border-x-0 border-300 border-bottom-0',
        unread: !n.read,
        info: n.info,
        type: n.info.type,
        outLink: getOutLink(n.info),
        inLink: getInLink(n.info)
      }))
    );

    setEarlierNotifications(
      seenNotifications.map(n => ({
        id: n.id,
        children: `<strong>${n.title}</strong>: ${n.body}`,
        time: getTime(n.created_at),
        emoji: getEmoji(n.info.type),
        className: 'rounded-0 border-x-0 border-300 border-bottom-0',
        unread: !n.read,
        info: n.info,
        type: n.info.type,
        outLink: getOutLink(n.info),
        inLink: getInLink(n.info)
      }))
    );

    unseenNotifications.length && setIsAllSeen(false);
  }, [notifications]);

  useEffect(() => {
    isOpen && setIsAllSeen(true);
  }, [isOpen]);

  useEffect(() => {
    userLogged && dispatch(getNotifications(userLogged.id));
  }, [userLogged]);

  useEffect(() => {
    socket &&
      socket.on('notification', () => {
        dispatch(getNotifications(userLogged?.id));
        messageHandler('success', '¡Nueva notificación!');
      });
  }, [socket]);

  const markAllNotisAsRead = e => {
    e.preventDefault();

    const updatedNewNotifications = newNotifications.map(notification =>
      Object.prototype.hasOwnProperty.call(notification, 'unread')
        ? { ...notification, unread: false }
        : notification
    );
    const updatedEarlierNotifications = earlierNotifications.map(notification =>
      Object.prototype.hasOwnProperty.call(notification, 'unread')
        ? { ...notification, unread: false }
        : notification
    );

    setNewNotifications(updatedNewNotifications);
    setEarlierNotifications(updatedEarlierNotifications);
    setIsAllSeen(true);
    handleToggle(!isOpen);
    dispatch(markAllAsRead(userLogged.id));
  };

  return (
    <>
      <Dropdown navbar={true} as="li" show={isOpen} onToggle={handleToggle}>
        <Dropdown.Toggle
          bsPrefix="toggle"
          as={Link}
          to="#!"
          className={classNames('p-0 nav-link', {
            'notification-indicator notification-indicator-primary': !isAllSeen
          })}
        >
          <FontAwesomeIcon icon="bell" transform="shrink-6" className="fs-4" />
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="dropdown-menu-card dropdown-menu-card-notification dropdown-menu-end dropdown-caret-bg bg-white"
          style={{ boxShadow: 'rgba(0, 0, 0, 0.5) 0.5px 0.5px 2px 0px' }}
        >
          <Card
            className="dropdown-menu-notification dropdown-menu-end shadow-none"
            style={{ maxWidth: '20rem' }}
          >
            <FalconCardHeader
              className="card-header"
              title="Notificaciones"
              titleTag="h6"
              light={false}
              endEl={
                <Link
                  className="card-link fw-normal"
                  to="#!"
                  onClick={markAllNotisAsRead}
                >
                  Marcar como leídas
                </Link>
              }
            />
            <ListGroup
              variant="flush"
              className="fw-normal fs--1 scrollbar"
              style={{ maxHeight: '19rem' }}
            >
              {isIterableArray(newNotifications) && (
                <>
                  <div className="list-group-title">NUEVAS</div>{' '}
                  {newNotifications.map(notification => (
                    <ListGroup.Item
                      key={notification.id}
                      onClick={() => handleClick(notification)}
                    >
                      <Notification {...notification} flush />
                    </ListGroup.Item>
                  ))}
                </>
              )}

              {isIterableArray(earlierNotifications) && (
                <>
                  <div className="list-group-title">VISTAS</div>
                  {earlierNotifications.map(notification => (
                    <ListGroup.Item
                      key={notification.id}
                      onClick={() => handleClick(notification)}
                    >
                      <Notification {...notification} flush />
                    </ListGroup.Item>
                  ))}
                </>
              )}

              {!notifications?.length && (
                <div className="d-flex flex-column align-items-center py-3">
                  <ImHeartBroken className="text-1000 fs-3" />
                  <p className="text-1000 fw-bold fs-1">
                    No tienes notificaciones
                  </p>
                </div>
              )}
            </ListGroup>
            <div
              className="card-footer text-center border-top"
              onClick={handleToggle}
            >
              <Link className="card-link d-block" to="#!">
                Ver todas las notificaciones
              </Link>
            </div>
          </Card>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

NotificationDropdown.propTypes = {
  establishment: PropTypes.object
};

export default NotificationDropdown;
